/**
 * What the builder edits but the Workflows API document cannot hold yet, kept
 * in this browser per version: step forms with field types, options and roles
 * beyond `FieldDef`, transition rules beyond the MVP kinds (role and department
 * approvers, actions, validators, audience restrictions, input) and who wrote
 * an automation rule. `mappers.ts` sends the part the document holds and
 * trusts an entry only while that part still matches the server, so an edit
 * made elsewhere (another browser, the agent) wins over a stale entry.
 *
 * Delete this file with the rest of the fixture once the document carries
 * these fields.
 */
import type {
  AutomationRule,
  StepForm,
  TransitionRules,
  WorkflowAuthor,
  WorkflowVersionOrigin,
} from '../types';

export interface AutomationRuleMeta {
  origin: WorkflowVersionOrigin;
  createdBy: WorkflowAuthor | null;
  createdAt: string;
}

export interface VersionSidecar {
  /** By state key. */
  stepForms: Record<string, StepForm>;
  /** By transition key. */
  transitionRules: Record<string, TransitionRules>;
  /** By automation key (`AutomationRule.id`). */
  automations: Record<string, AutomationRuleMeta>;
}

export const WORKFLOW_BUILDER_SIDECAR_KEY =
  'gasbi:crm:workflow-builder-sidecar:v1';

type SidecarDb = Record<string, VersionSidecar>;

let memoryDb: SidecarDb | null = null;

export const emptySidecar = (): VersionSidecar => ({
  stepForms: {},
  transitionRules: {},
  automations: {},
});

const readDb = (): SidecarDb => {
  if (memoryDb) return memoryDb;

  try {
    const raw = localStorage.getItem(WORKFLOW_BUILDER_SIDECAR_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    memoryDb =
      parsed && typeof parsed === 'object' ? (parsed as SidecarDb) : {};
  } catch {
    memoryDb = {};
  }

  return memoryDb;
};

const writeDb = (db: SidecarDb) => {
  memoryDb = db;

  try {
    localStorage.setItem(WORKFLOW_BUILDER_SIDECAR_KEY, JSON.stringify(db));
  } catch {
    // Storage unavailable: the entries live in memory for this session only.
  }
};

export const readVersionSidecar = (versionId: string): VersionSidecar => ({
  ...emptySidecar(),
  ...readDb()[versionId],
});

/** Merges the given parts into the version's entry; a part replaces its keys. */
export const updateVersionSidecar = (
  versionId: string,
  patch: Partial<VersionSidecar>,
) => {
  const db = readDb();
  const current = readVersionSidecar(versionId);

  writeDb({
    ...db,
    [versionId]: {
      stepForms: { ...current.stepForms, ...patch.stepForms },
      transitionRules: { ...current.transitionRules, ...patch.transitionRules },
      automations: { ...current.automations, ...patch.automations },
    },
  });
};

/**
 * Drops the entries of states and transitions the saved document no longer
 * has, so a deleted one leaves nothing behind — and a later one reusing its key
 * starts clean.
 */
export const pruneVersionSidecar = (
  versionId: string,
  document: { states: { key: string }[]; transitions: { key: string }[] },
) => {
  const db = readDb();
  const current = readVersionSidecar(versionId);
  const keep = <T>(entries: Record<string, T>, keys: { key: string }[]) => {
    const kept = new Set(keys.map(item => item.key));
    return Object.fromEntries(
      Object.entries(entries).filter(([key]) => kept.has(key)),
    );
  };

  writeDb({
    ...db,
    [versionId]: {
      ...current,
      stepForms: keep(current.stepForms, document.states),
      transitionRules: keep(current.transitionRules, document.transitions),
    },
  });
};

/** A draft created from a published version starts with its entries. */
export const copyVersionSidecar = (
  fromVersionId: string,
  toVersionId: string,
) => {
  const db = readDb();
  const source = db[fromVersionId];
  if (!source) return;

  writeDb({ ...db, [toVersionId]: structuredClone(source) });
};

/** Keeps the automation meta of the rules still in the list, by key. */
export const automationMetaOf = (
  rules: AutomationRule[],
): Record<string, AutomationRuleMeta> =>
  Object.fromEntries(
    rules.map(rule => [
      rule.id,
      {
        origin: rule.origin,
        createdBy: rule.createdBy,
        createdAt: rule.createdAt,
      },
    ]),
  );

/** Tests only. */
export const resetSidecarForTests = () => {
  memoryDb = null;
};
